import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const categoryToSlug = (cat) =>
  (cat || '').toLowerCase().replace(/&/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const CATEGORIES = [
  { name: 'Income Tax', icon: 'fa-file-invoice-dollar' },
  { name: 'GST', icon: 'fa-receipt' },
  { name: 'Corporate Law', icon: 'fa-building-columns' },
  { name: 'Audit & Assurance', icon: 'fa-magnifying-glass-chart' },
  { name: 'Financial Planning', icon: 'fa-chart-pie' },
  { name: 'Compliance', icon: 'fa-shield-check' },
  { name: 'Budget & Finance', icon: 'fa-coins' },
  { name: 'Investment Advisory', icon: 'fa-chart-line' },
  { name: 'ROC & MCA', icon: 'fa-landmark' },
  { name: 'TDS & TCS', icon: 'fa-percent' },
  { name: 'International Taxation', icon: 'fa-globe' },
  { name: 'Startups', icon: 'fa-rocket' },
  { name: 'News', icon: 'fa-newspaper' },
];

const BlogSidebar = ({
  onCategorySelect,
  onTagSelect,
  currentCategory,
  currentTag,
}) => {
  const [moreBlogs, setMoreBlogs] = useState([]);
  const [popularTags, setPopularTags] = useState([]);
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMoreBlogs();
    fetchPopularTags();
  }, []);

  const fetchMoreBlogs = async () => {
    try {
      // fetch a pool of 20 and pick 5 at random
      const response = await fetch(
        `${API_URL}/api/blogs?isActive=true&limit=20&sortBy=createdAt&order=desc`
      );
      const data = await response.json();
      if (data.success && data.data.length > 0) {
        const shuffled = [...data.data].sort(() => Math.random() - 0.5);
        setMoreBlogs(shuffled.slice(0, 5));
      }
    } catch (error) {
      console.error('Error fetching more blogs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPopularTags = async () => {
    try {
      const response = await fetch(`${API_URL}/api/blogs?isActive=true&limit=50`);
      const data = await response.json();
      if (data.success) {
        const tagCounts = {};
        data.data.forEach((blog) => {
          (blog.tags || []).forEach((tag) => {
            tagCounts[tag] = (tagCounts[tag] || 0) + 1;
          });
        });
        const sortedTags = Object.entries(tagCounts)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 15)
          .map(([tag]) => tag);
        setPopularTags(sortedTags);
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email) {
      setSubscribed(true);
      setEmail('');
      setTimeout(() => setSubscribed(false), 5000);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const handleClick = () => window.scrollTo(10, 0);

  return (
    <aside className="blog_sidebar">
      {/* Categories Widget */}
      <div className="sidebar_widget sidebar_categories">
        <h3 className="sidebar_widget__title">
          <i className="fa-solid fa-folder-tree"></i>
          Categories
        </h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', padding: '4px 0' }}>
          {CATEGORIES.map((cat) => {
            const isActive = currentCategory === cat.name;
            return (
              <button
                key={cat.name}
                onClick={() => onCategorySelect(cat.name === currentCategory ? '' : cat.name)}
                style={{
                  padding: '5px 12px',
                  fontSize: '12px',
                  fontWeight: 600,
                  borderRadius: '20px',
                  border: `1.5px solid ${isActive ? '#2563a8' : '#d1d9e0'}`,
                  background: isActive ? '#2563a8' : '#fff',
                  color: isActive ? '#fff' : '#25343F',
                  cursor: 'pointer',
                  transition: 'all 0.18s ease',
                  whiteSpace: 'nowrap',
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.borderColor = '#2563a8';
                    e.currentTarget.style.color = '#2563a8';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.borderColor = '#d1d9e0';
                    e.currentTarget.style.color = '#25343F';
                  }
                }}
              >
                {cat.name}
              </button>
            );
          })}
        </div>
      </div>

      {/* More Blogs Widget */}
      <div className="sidebar_widget sidebar_recent">
        <h3 className="sidebar_widget__title">
          <i className="fa-solid fa-layer-group"></i>
          More Blogs
        </h3>
        {loading ? (
          <div className="sidebar_loading">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="recent_post_skeleton">
                <div className="skeleton_thumb"></div>
                <div className="skeleton_info">
                  <div className="skeleton_line"></div>
                  <div className="skeleton_line short"></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <ul className="sidebar_recent__list">
            {moreBlogs.map((post) => (
              <li key={post._id || post.slug}>
                <Link
                  to={`/${post.type || 'blog'}/${categoryToSlug(post.category)}/${post.slug}`}
                  className="sidebar_recent__item"
                  onClick={handleClick}
                >
                  <div className="recent_post__thumb">
                    {post.featuredImage?.fileId ? (
                      <img
                        src={`${API_URL}/api/upload/file/${post.featuredImage.fileId}`}
                        alt={post.title}
                        loading="lazy"
                      />
                    ) : (
                      <div className="recent_post__thumb_placeholder">
                        <i className="fa-regular fa-image"></i>
                      </div>
                    )}
                  </div>
                  <div className="recent_post__info">
                    <h4 className="recent_post__title">{post.title}</h4>
                    <div className="recent_post__meta">
                      <span className="recent_post__date">
                        <i className="fa-regular fa-calendar"></i>
                        {formatDate(post.publishedAt)}
                      </span>
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Tags Widget */}
      {popularTags.length > 0 && (
        <div className="sidebar_widget sidebar_tags">
          <h3 className="sidebar_widget__title">
            <i className="fa-solid fa-tags"></i>
            Popular Tags
          </h3>
          <div className="sidebar_tags__cloud">
            {popularTags.map((tag) => (
              <button
                key={tag}
                className={`sidebar_tag ${currentTag === tag ? 'active' : ''}`}
                onClick={() => onTagSelect(tag === currentTag ? '' : tag)}
              >
                #{tag}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Newsletter Widget */}
      <div className="sidebar_widget sidebar_newsletter">
        <div className="newsletter_content">
          <div className="newsletter_icon">
            <i className="fa-solid fa-envelope-open-text"></i>
          </div>
          <h3 className="newsletter_title">Stay Informed</h3>
          <p className="newsletter_text">
            Subscribe for the latest updates on tax laws, compliance, and financial planning.
          </p>
          {subscribed ? (
            <div className="newsletter_success">
              <i className="fa-solid fa-circle-check"></i>
              <span>Thank you for subscribing!</span>
            </div>
          ) : (
            <form className="newsletter_form" onSubmit={handleSubscribe}>
              <input
                type="email"
                placeholder="Your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="newsletter_input"
              />
              <button type="submit" className="newsletter_btn">
                Subscribe
                <i className="fa-solid fa-paper-plane"></i>
              </button>
            </form>
          )}
          <p className="newsletter_privacy">
            <i className="fa-solid fa-lock"></i>
            We respect your privacy. Unsubscribe anytime.
          </p>
        </div>
      </div>
    </aside>
  );
};

export default BlogSidebar;
